import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { router } from 'expo-router';
import Text from '@/components/Typography/Text';
import SettingsSection from '@/components/SettingsSection';
import PersonalizationSettings from './PersonalizationSettings';
import MacNotificationSettings from './MacNotificationSettings';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { LANGUAGES } from '@/constants/translations';
import { useUIStore, useChatStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { authApi, accountApi } from '@/lib/api';
import { useTranslation, selectLanguage } from '@/hooks/useTranslation';
import Button from '@/components/Buttons/Button';
import DropdownMenuCheckboxItem from '@/components/DropdownMenu/DropdownMenuCheckboxItem';
import SwitchInput from '@/components/Inputs/Switch';
import { previewWorkNotificationSound } from '@/lib/workNotificationSound';
import Badge from '@/components/Typography/Badge';
import { exportAccountData } from '@/constants/utils/accountDataExport';

const styles = StyleSheet.create({
  menuBarSetting: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  errorText: {
    marginTop: 4,
  },
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  archiveActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
});

function GeneralSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const workNotificationSounds = useUIStore(state => state.workNotificationSounds);
  const setWorkNotificationSounds = useUIStore(state => state.setWorkNotificationSounds);
  const language = useUIStore(state => state.language);
  const clearSession = useAuthStore(state => state.clearSession);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isEmailVerified = useAuthStore(state => state.user?.flags?.isEmailVerified);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verifyEmailError, setVerifyEmailError] = useState(null);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const clearChats = useChatStore(state => state.clearChats);
  const archiveAllChats = useChatStore(state => state.archiveAllChats);
  const unarchiveAllChats = useChatStore(state => state.unarchiveAllChats);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExportingAccountData, setIsExportingAccountData] = useState(false);

  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();

  // Check if running as installed PWA (hide Install App button if already installed)
  const [isStandalone, setIsStandalone] = useState(true); // default hidden
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    setIsStandalone(standalone);
  }, []);

  const handleInstallApp = () => {
    openModal('InstallAppModal');
  };

  const handleBulkOperation = async (operation, successMessage) => {
    try {
      setIsProcessing(true);
      await operation();
      addToast(successMessage, 'success');
    } catch (error) {
      addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleArchiveAll = () => {
    if (!isPlus) {
      openModal('PricingModal', {
        title: t('upgrade.title'),
        subtitle: t('settings.general.upgradeToArchive'),
      });
      return;
    }

    showInputModal({
      title: t('settings.general.dangerZone.archiveAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.archiveAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.archiveAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.archiveAllConversations(password);
          if (!response.error) {
            archiveAllChats();
            addToast(t('settings.general.allArchivedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleUnarchiveAll = () => {
    showInputModal({
      title: t('settings.general.dangerZone.unarchiveAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.unarchiveAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.unarchiveAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.unarchiveAllConversations(password);
          if (!response.error) {
            unarchiveAllChats();
            addToast(t('settings.general.allUnarchivedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleDeleteAll = () => {
    if (!isPlus) {
      openModal('PricingModal', {
        title: t('upgrade.title'),
        subtitle: t('settings.general.upgradeToDelete'),
      });
      return;
    }

    showInputModal({
      title: t('settings.general.dangerZone.deleteAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.deleteAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.deleteAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.deleteAllConversations(password);
          if (!response.error) {
            clearChats();
            addToast(t('settings.general.allDeletedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleDeleteAccount = () => {
    if (isPlus) {
      openModal('CancelSubscriptionGuideModal');
      return;
    }

    showInputModal({
      title: t('settings.general.dangerZone.deleteAccount.confirmTitle'),
      subtitle: t('settings.general.dangerZone.deleteAccount.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.deleteAccount.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.deleteAccount(password, 'User requested account deletion');
          if (!response.error) {
            addToast(t('settings.general.accountDeletedSuccess'), 'success');
            setTimeout(() => {
              clearSession();
              router.replace({ pathname: '/', params: { transition: 'fade_from_bottom' } });
            }, 300);
            return;
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleExportAccountData = async () => {
    if (isExportingAccountData) return;
    setIsExportingAccountData(true);
    try {
      await exportAccountData();
      const successMessage = Platform.OS === 'web'
        ? t('settings.general.dataExport.successToast')
        : t('settings.general.dataExport.successToastNative');
      addToast(successMessage, 'success');
      // Truncation notes are surfaced inside the archive's README rather
      // than as a follow-up toast; keeps the UI quiet for the common case
      // where nothing was capped.
    } catch (error) {
      addToast(error?.message || t('settings.general.dataExport.errorToast'), 'error');
    } finally {
      setIsExportingAccountData(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    setIsVerifyingEmail(true);
    setVerifyEmailError(null);
    try {
      const response = await authApi.resendVerificationEmail();
      if (!response.error) {
        addToast(t('settings.general.verificationEmailSent'), 'success');
      } else {
        addToast(response.message || t('settings.general.verificationEmailError'), 'error');
      }
    } catch (error) {
      setVerifyEmailError(error.message || t('settings.general.verificationEmailError'));
      addToast(error.message || t('settings.general.verificationEmailError'), 'error');
      console.error('Error sending verification email:', error);
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleLogout = () => {
    showConfirmationModal({
      title: t('settings.general.logout.confirmTitle'),
      message: t('settings.general.logout.confirmMessage'),
      confirmText: t('settings.general.logout.confirmTitle'),
      isDangerous: true,
      onConfirm: async () => {
        await clearSession();
        setTimeout(() => {
          router.replace({ pathname: '/', params: { transition: 'fade_from_bottom' } });
        }, 100);
      }
    });
  };

  return (
    <View>
      {Platform.OS === 'web' && !isStandalone && (
        <SettingsSection
          title={t('settings.general.installApp')}
          subtitle={t('settings.general.installAppSubtitle')}
          isFirst
        >
          <Button
            text={t('settings.general.install')}
            variant="secondary"
            size="small"
            prependIcon="mobile"
            onPress={handleInstallApp}
          />
        </SettingsSection>
      )}

      <SettingsSection
        title={t('settings.general.language.title')}
        isFirst={Platform.OS !== 'web' || isStandalone}
        isLast={isGhost}
      >
        <View style={styles.menuBarSetting}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild={Platform.OS === 'macos'}>
              <Button
                text={language.toUpperCase()}
                variant="secondary"
                size="small"
                appendIcon="chevron-down"
                color={colors.text_color_primary}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              {Object.values(LANGUAGES).map(lang => (
                <DropdownMenuCheckboxItem
                  key={lang.value}
                  onValueChange={() => { selectLanguage(lang.value).catch(() => addToast(t('settings.general.errors.operationFailed'), 'error')); }}
                  isActive={language === lang.value}
                  label={lang.label}
                />
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </View>
      </SettingsSection>
      
      <MacNotificationSettings />
      {Platform.OS === 'web' && (
        <SettingsSection
          title={t('settings.general.notificationSounds.title')}
          subtitle={t('settings.general.notificationSounds.subtitle')}
        >
          <View style={styles.archiveActions}>
            <SwitchInput
              accessibilityLabel={t('settings.general.notificationSounds.title')}
              value={workNotificationSounds !== false}
              onValueChange={setWorkNotificationSounds}
            />
            <Button
              text={t('settings.general.notificationSounds.preview')}
              variant="secondary"
              size="small"
              isDisabled={workNotificationSounds === false}
              onPress={previewWorkNotificationSound}
            />
          </View>
        </SettingsSection>
      )}

      {!isGhost && <PersonalizationSettings embeddedInPage embeddedInGeneral />}

      {!isGhost && !isEmailVerified && (
        <>
          <SettingsSection 
            title={t('settings.general.verifyEmail.title')} 
            subtitle={t('settings.general.verifyEmail.subtitle')} 
          >
            <View>
              <Button
                text={t('settings.general.verifyEmail.button')}
                variant="secondary"
                size="small"
                isLoading={isVerifyingEmail}
                isDisabled={isVerifyingEmail}
                onPress={handleSendVerificationEmail}
              />
              {verifyEmailError && (
                <Text size='xs' color='danger' style={styles.errorText}>{verifyEmailError}</Text>
              )}
            </View>
          </SettingsSection>
        </>
      )}

      {!isGhost && (
        <SettingsSection
          title={t('settings.general.dataExport.title')}
          subtitle={t('settings.general.dataExport.subtitle')}
          isLast
        >
          <Button
            text={t('settings.general.dataExport.button')}
            variant="secondary"
            size="small"
            prependIcon="download"
            isLoading={isExportingAccountData}
            isDisabled={isExportingAccountData}
            onPress={handleExportAccountData}
          />
        </SettingsSection>
      )}

      {!isGhost && (
      <View style={{
        borderTopWidth: 0.5,
        borderBottomWidth: 0.5,
        borderColor: colors.color_destructive,
        paddingTop: 10,
        paddingBottom: 10,
        marginTop: 10,
        marginBottom: 10,
        overflow: 'visible'
      }}>
        <Text size='xs' color='danger' weight='bold' style={{
          marginBottom: 5,
          marginTop:-22,
          marginHorizontal: 'auto',
          paddingLeft: 10,
          width: 105,
          backgroundColor: colors.solid_color_primary,
          paddingVertical: 5,
          borderRadius: 100,
        }}>
          {t('settings.general.dangerZone.title')}
        </Text>

        <SettingsSection title={
          <View style={styles.sectionTitleContainer}>
            <Text>{t('settings.general.dangerZone.archiveAll.title')}</Text>
            {!isPlus && (
              <Badge
                text="PLUS"
                onPress={handleArchiveAll}
                icon="sparkles"
                size="small"
                isPlus
              />
            )}
          </View>
        }>
          <View style={styles.archiveActions}>
            <Button
              text={t('settings.general.dangerZone.archiveAll.button')}
              variant="secondary"
              size="small"
              isLoading={isProcessing}
              isDisabled={isProcessing}
              onPress={handleArchiveAll}
            />
            <Button
              text={t('settings.general.dangerZone.unarchiveAll.button')}
              variant="secondary"
              size="small"
              isLoading={isProcessing}
              isDisabled={isProcessing}
              onPress={handleUnarchiveAll}
            />
          </View>
        </SettingsSection>

        <SettingsSection title={
          <View style={styles.sectionTitleContainer}>
            <Text>{t('settings.general.dangerZone.deleteAll.title')}</Text>
            {!isPlus && (
              <Badge
                text="PLUS"
                onPress={handleDeleteAll}
                icon="sparkles"
                size="small"
                isPlus
              />
            )}
          </View>
        }>
          <Button
            text={t('settings.general.dangerZone.deleteAll.button')}
            variant="danger"
            size="small"
            isLoading={isProcessing}
            isDisabled={isProcessing}
            onPress={handleDeleteAll}
          />
        </SettingsSection>

        <SettingsSection title={t('settings.general.dangerZone.deleteAccount.title')} isLast>
          <Button
            text={t('settings.general.dangerZone.deleteAccount.button')}
            variant="danger"
            size="small"
            onPress={handleDeleteAccount}
          />
          </SettingsSection>
        </View>
      )}

      <SettingsSection title={t('settings.general.logout.title')} isLast>
        <Button
          text={t('settings.general.logout.button')}
          variant="secondary"
          size="small"
          onPress={handleLogout}
        />
      </SettingsSection>
    </View>
  );
}

export default React.memo(GeneralSettings);
